import { useEffect, useState } from "react";
import { LayoutDashboard, Boxes, ShoppingBag, Users, Plug, DollarSign, Search, Plus, Trash2, TrendingUp, Package, Bot, RefreshCw, Send, Truck, MapPin, Pencil, Download, Upload, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CY = "#00F0FF", PU = "#8A2BE2", COLORS = ["#00F0FF", "#8A2BE2", "#00E676", "#FFB800", "#FF2A6D"];
const STATUSES = ["paid", "processing", "shipped", "delivered"];

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="dw-card rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: `${accent}1a`, color: accent }}><Icon className="h-5 w-5" /></div>
      </div>
      <p className="mt-4 font-mono text-2xl font-bold text-white">{value}</p>
      <p className="mt-1 text-xs uppercase tracking-wider text-slate-400">{label}</p>
    </div>
  );
}

function Dashboard() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setA(data)).catch(() => {}); }, []);
  if (!a) return <div className="py-16 text-center text-slate-500">Loading analytics…</div>;
  const statusData = STATUSES.map((s) => ({ name: s, value: a.orders_by_status?.[s] || 0 }));
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={DollarSign} label="Revenue (USD)" value={`$${a.revenue_usd.toLocaleString()}`} accent={CY} />
        <Stat icon={ShoppingBag} label="Total Orders" value={a.orders_total} accent={PU} />
        <Stat icon={Users} label="Customers" value={a.customers_total} accent="#00E676" />
        <Stat icon={Boxes} label="Products" value={a.products_total} accent="#FFB800" />
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="dw-card rounded-2xl p-6">
          <h3 className="font-display mb-4 text-sm font-semibold text-white">Top Products</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={a.top_products}>
              <XAxis dataKey="name" tick={{ fill: "#64748B", fontSize: 10 }} interval={0} tickFormatter={(v) => v.split(" ")[0]} />
              <YAxis tick={{ fill: "#64748B", fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#111420", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
              <Bar dataKey="qty" fill={CY} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="dw-card rounded-2xl p-6">
          <h3 className="font-display mb-4 text-sm font-semibold text-white">Orders by Status</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={3}>
                {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#111420", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            {statusData.map((s, i) => <span key={s.name} className="flex items-center gap-1.5 text-xs text-slate-400"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /> {s.name} ({s.value})</span>)}
          </div>
        </div>
      </div>
      <div className="dw-card rounded-2xl p-6">
        <h3 className="font-display mb-4 text-sm font-semibold text-white">Recent Orders · AOV ${a.avg_order_value}</h3>
        <div className="space-y-2">
          {a.recent_orders?.length ? a.recent_orders.map((o) => (
            <div key={o.order_id} className="flex items-center justify-between rounded-lg border border-white/5 px-4 py-2.5 text-sm">
              <span className="font-mono text-[#00F0FF]">#{o.order_id}</span>
              <span className="text-slate-400">{o.email}</span>
              <span className="font-mono text-slate-200">${o.total_usd}</span>
              <span className="rounded-full bg-white/5 px-2 py-0.5 font-mono text-[10px] uppercase text-slate-400">{o.status}</span>
            </div>
          )) : <p className="text-sm text-slate-500">No orders yet.</p>}
        </div>
      </div>
    </div>
  );
}

function Catalog() {
  const { user } = useAuth();
  const isOwner = ["owner", "admin"].includes(user?.role);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", category: "electronics", price_usd: "", compare_at_usd: "", images: "", description: "", trending: false, featured: false, coming_soon: false });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const load = () => api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd);
      setForm((prev) => ({ ...prev, images: data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const create = async () => {
    try {
      await api.post("/admin/products", { ...form, price_usd: parseFloat(form.price_usd) || 0, compare_at_usd: parseFloat(form.compare_at_usd) || null, images: form.images ? [form.images] : ["https://images.unsplash.com/photo-1600998837340-4887228e311f?w=600"] });
      toast.success("Product created"); setOpen(false); load();
      setForm({ name: "", category: "electronics", price_usd: "", compare_at_usd: "", images: "", description: "", trending: false, featured: false, coming_soon: false });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); };
  const toggleLive = async (p) => {
    try { await api.put(`/admin/products/${p.product_id}`, { coming_soon: !p.coming_soon }); toast.success(!p.coming_soon ? "Marked Coming Soon" : "Now live & available"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const openEdit = (p) => { setEditForm({ ...p, images: p.images?.[0] || "" }); setEditOpen(true); };
  const editUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/admin/upload", fd); setEditForm((p) => ({ ...p, images: data.url })); toast.success("Image uploaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/admin/products/${editForm.product_id}`, {
        name: editForm.name, category: editForm.category,
        price_usd: parseFloat(editForm.price_usd) || 0,
        compare_at_usd: editForm.compare_at_usd ? parseFloat(editForm.compare_at_usd) : null,
        description: editForm.description || "", images: editForm.images ? [editForm.images] : [],
        coming_soon: !!editForm.coming_soon, trending: !!editForm.trending,
      });
      toast.success("Product updated"); setEditOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const exportProducts = async () => {
    const { data } = await api.get("/admin/products");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "dropworldwide-products.json"; a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${data.length} products`);
  };
  const importProducts = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const parsed = JSON.parse(await f.text());
      const list = Array.isArray(parsed) ? parsed : (parsed.products || []);
      const { data } = await api.post("/admin/products/import", { products: list });
      toast.success(`Imported ${data.imported} products`); load();
    } catch (err) { toast.error("Import failed — please choose a valid export file"); }
    e.target.value = "";
  };
  const clearDemos = async () => {
    if (!window.confirm("Remove the built-in demo/sample products? Your own products stay.")) return;
    try { const { data } = await api.post("/admin/products/clear-demo"); toast.success(`Removed ${data.deleted} demo products`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const clearAll = async () => {
    if (!window.confirm("Delete ALL products currently listed (including your own)? Tip: Export first so you can re-import.")) return;
    try { const { data } = await api.post("/admin/products/clear"); toast.success(`Deleted ${data.deleted} products`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-catalog">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-lg font-semibold text-white">Product Catalog ({products.length})</h3>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={exportProducts} variant="outline" className="rounded-xl border-white/15 bg-transparent text-slate-200 hover:bg-white/5" data-testid="admin-export"><Download className="mr-1 h-4 w-4" /> Export</Button>
          <label className="flex cursor-pointer items-center gap-1 rounded-xl border border-white/15 px-4 py-2 text-sm text-slate-200 hover:bg-white/5" data-testid="admin-import-label"><Upload className="h-4 w-4" /> Import<input type="file" accept="application/json,.json" className="hidden" onChange={importProducts} data-testid="admin-import" /></label>
          {isOwner && (<>
            <Button onClick={clearDemos} variant="outline" className="rounded-xl border-[#FFB800]/40 bg-transparent text-[#FFB800] hover:bg-[#FFB800]/10" data-testid="admin-clear-demo">Remove Demos</Button>
            <Button onClick={clearAll} variant="outline" className="rounded-xl border-[#FF2A6D]/40 bg-transparent text-[#FF6B9D] hover:bg-[#FF2A6D]/10" data-testid="admin-clear"><Trash2 className="mr-1 h-4 w-4" /> Delete All</Button>
          </>)}
          <Button onClick={() => setOpen(true)} className="rounded-xl bg-[#00F0FF] text-[#08090E] hover:bg-[#33F3FF]" data-testid="admin-add-product"><Plus className="mr-1 h-4 w-4" /> Add Product</Button>
        </div>
      </div>
      <div className="dw-card overflow-hidden rounded-2xl">
        <div className="max-h-[560px] overflow-y-auto">
          {products.map((p) => (
            <div key={p.product_id} className="flex items-center gap-3 border-b border-white/5 px-4 py-3">
              <img src={p.images?.[0]} alt="" className="h-10 w-10 rounded-lg object-cover" />
              <div className="flex-1 min-w-0"><p className="truncate text-sm text-slate-200">{p.name}</p><p className="font-mono text-xs text-slate-500 capitalize">{p.category.replace("-", " ")} · {p.inventory_status}</p></div>
              <span className="font-mono text-sm text-white">${p.price_usd}</span>
              <label className="flex items-center gap-1.5 text-[11px]" title="Toggle Coming Soon / Live">
                <Switch checked={!p.coming_soon} onCheckedChange={() => toggleLive(p)} data-testid={`live-toggle-${p.product_id}`} />
                <span className={p.coming_soon ? "text-[#FFB800]" : "text-[#00E676]"}>{p.coming_soon ? "Coming Soon" : "Live"}</span>
              </label>
              <button onClick={() => openEdit(p)} className="text-slate-500 hover:text-[#00F0FF]" data-testid={`admin-edit-${p.product_id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(p.product_id)} className="text-slate-500 hover:text-[#FF2A6D]" data-testid={`admin-delete-${p.product_id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[#111420] text-slate-100">
          <DialogHeader><DialogTitle className="font-display">New Product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="np-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1 border-white/10 bg-white/5" data-testid="np-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#111420] text-slate-200">
                    {["electronics", "wearables", "apparel", "smart-home", "travel-gear", "modern-office"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Price USD</Label><Input type="number" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="np-price" /></div>
            </div>
            <div>
              <Label>Product Image</Label>
              <div className="mt-1 flex gap-2">
                <Input value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className="border-white/10 bg-white/5" placeholder="Upload a photo → or paste an https:// link" data-testid="np-image" />
                <label className="flex shrink-0 cursor-pointer items-center gap-1 rounded-md bg-[#00F0FF] px-3 text-xs font-bold text-[#08090E] hover:bg-[#33F3FF]">
                  <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="np-upload" />
                  {uploading ? "…" : "Upload"}
                </label>
              </div>
              {form.images && <img src={form.images} alt="" className="mt-2 h-20 w-20 rounded-lg object-cover" />}
            </div>
            <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 border-white/10 bg-white/5" placeholder="Describe the product for customers" data-testid="np-description" /></div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.trending} onCheckedChange={(v) => setForm({ ...form, trending: v })} data-testid="np-trending" /> Trending</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.coming_soon} onCheckedChange={(v) => setForm({ ...form, coming_soon: v })} data-testid="np-coming" /> Coming Soon</label>
            </div>
            <Button onClick={create} className="w-full rounded-xl bg-[#00F0FF] text-[#08090E] hover:bg-[#33F3FF]" data-testid="np-save">Create Product</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="border-white/10 bg-[#111420] text-slate-100">
          <DialogHeader><DialogTitle className="font-display">Edit Product</DialogTitle></DialogHeader>
          {editForm && (
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="ep-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Price USD</Label><Input type="number" value={editForm.price_usd} onChange={(e) => setEditForm({ ...editForm, price_usd: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="ep-price" /></div>
              <div><Label>Compare-at (optional)</Label><Input type="number" value={editForm.compare_at_usd || ""} onChange={(e) => setEditForm({ ...editForm, compare_at_usd: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="ep-compare" /></div>
            </div>
            <div><Label>Description</Label><Textarea rows={3} value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="ep-description" /></div>
            <div>
              <Label>Product Image</Label>
              <div className="mt-1 flex gap-2">
                <Input value={editForm.images} onChange={(e) => setEditForm({ ...editForm, images: e.target.value })} className="border-white/10 bg-white/5" placeholder="Upload or paste https:// link" data-testid="ep-image" />
                <label className="flex shrink-0 cursor-pointer items-center gap-1 rounded-md bg-[#00F0FF] px-3 text-xs font-bold text-[#08090E] hover:bg-[#33F3FF]"><input type="file" accept="image/*" className="hidden" onChange={editUpload} data-testid="ep-upload" /> Upload</label>
              </div>
              {editForm.images && <img src={editForm.images} alt="" className="mt-2 h-20 w-20 rounded-lg object-cover" />}
            </div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!editForm.trending} onCheckedChange={(v) => setEditForm({ ...editForm, trending: v })} data-testid="ep-trending" /> Trending</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!editForm.coming_soon} onCheckedChange={(v) => setEditForm({ ...editForm, coming_soon: v })} data-testid="ep-coming" /> Coming Soon</label>
            </div>
            <Button onClick={saveEdit} className="w-full rounded-xl bg-[#00F0FF] text-[#08090E] hover:bg-[#33F3FF]" data-testid="ep-save">Save Changes</Button>
          </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/orders/${id}/status`, { status }); toast.success(`Order marked ${status}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const [ship, setShip] = useState({});
  const markShipped = async (id) => {
    const s = ship[id] || {};
    if (!s.tracking) { toast.error("Paste the tracking number from TeamDrop first"); return; }
    try {
      await api.put(`/admin/orders/${id}/status`, { status: "shipped", tracking_number: s.tracking, carrier: s.carrier || "TeamDrop" });
      toast.success("Marked shipped — customer emailed with tracking");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const fmtAddr = (a) => [a?.address, a?.city, a?.country, a?.zip].filter(Boolean).join(", ");
  return (
    <div data-testid="admin-orders">
      <h3 className="font-display mb-4 text-lg font-semibold text-white">Orders & Fulfilment ({orders.length})</h3>
      <div className="space-y-3">
        {orders.length === 0 && <div className="dw-card rounded-2xl p-10 text-center text-slate-500">No orders yet.</div>}
        {orders.map((o) => (
          <div key={o.order_id} className="dw-card rounded-2xl p-5" data-testid={`admin-order-${o.order_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div><p className="font-mono text-sm text-[#00F0FF]">#{o.order_id}</p><p className="text-xs text-slate-500">{o.email} · {new Date(o.created_at).toLocaleString()}</p></div>
              <span className="font-mono text-sm font-bold text-white">${o.total_usd}</span>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-[#8A2BE2]/15 px-2 py-0.5 font-mono text-[10px] text-[#c79cff]">{o.supplier_routing?.provider || "TeamDrop"}</span>
                <Select value={o.status} onValueChange={(v) => setStatus(o.order_id, v)}>
                  <SelectTrigger className="h-8 w-36 border-white/10 bg-white/5 text-xs" data-testid={`order-status-${o.order_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#111420] text-slate-200">
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 grid gap-3 rounded-xl border border-white/10 bg-white/5 p-4 sm:grid-cols-2">
              <div>
                <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400"><MapPin className="h-3 w-3" /> Ship to (copy into TeamDrop)</p>
                <p className="mt-1 text-sm text-slate-200">{o.full_name || "—"}</p>
                <p className="text-xs text-slate-400">{fmtAddr(o.shipping_address) || "No address provided"}</p>
                <p className="text-xs text-slate-500">{o.email}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Items to order</p>
                {o.items?.map((i) => (
                  <p key={i.product_id} className="mt-1 text-xs text-slate-300">{i.name} × {i.quantity} <span className="text-slate-500">· SKU {i.supplier?.teamdrop_sku || "—"}</span></p>
                ))}
              </div>
            </div>
            {o.tracking_number ? (
              <p className="mt-3 flex items-center gap-1.5 font-mono text-xs text-[#00E676]"><Truck className="h-3.5 w-3.5" /> Shipped · {o.carrier} · {o.tracking_number} (customer notified)</p>
            ) : (
              <div className="mt-3 flex flex-wrap items-end gap-2">
                <div className="w-32"><Label className="text-[10px] text-slate-400">Carrier</Label><Input value={ship[o.order_id]?.carrier || ""} onChange={(e) => setShip({ ...ship, [o.order_id]: { ...ship[o.order_id], carrier: e.target.value } })} placeholder="e.g. DHL" className="h-8 border-white/10 bg-white/5 text-xs" data-testid={`carrier-${o.order_id}`} /></div>
                <div className="min-w-[160px] flex-1"><Label className="text-[10px] text-slate-400">Tracking number (from TeamDrop)</Label><Input value={ship[o.order_id]?.tracking || ""} onChange={(e) => setShip({ ...ship, [o.order_id]: { ...ship[o.order_id], tracking: e.target.value } })} placeholder="Paste tracking code" className="h-8 border-white/10 bg-white/5 font-mono text-xs" data-testid={`tracking-${o.order_id}`} /></div>
                <Button onClick={() => markShipped(o.order_id)} className="h-8 rounded-lg bg-[#00F0FF] text-xs font-bold text-[#08090E] hover:bg-[#33F3FF]" data-testid={`ship-${o.order_id}`}><Truck className="mr-1 h-3.5 w-3.5" /> Mark shipped & email customer</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Customers() {
  const [c, setC] = useState([]);
  useEffect(() => { api.get("/admin/customers").then(({ data }) => setC(data)).catch(() => {}); }, []);
  return (
    <div data-testid="admin-customers">
      <h3 className="font-display mb-4 text-lg font-semibold text-white">Customers ({c.length})</h3>
      <div className="dw-card overflow-hidden rounded-2xl">
        {c.length === 0 && <p className="p-10 text-center text-slate-500">No customers yet.</p>}
        {c.map((u) => (
          <div key={u.id} className="flex items-center gap-3 border-b border-white/5 px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#00F0FF] to-[#8A2BE2] text-xs font-bold text-[#08090E]">{(u.name || u.email)[0]?.toUpperCase()}</div>
            <div className="flex-1"><p className="text-sm text-slate-200">{u.name || "—"}</p><p className="text-xs text-slate-500">{u.email}</p></div>
            <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-slate-400">{u.auth_provider}</span>
            <span className="font-mono text-sm text-slate-300">{u.orders} orders</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Suppliers() {
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState([]);
  const [creds, setCreds] = useState({});
  const load = () => {
    api.get("/admin/suppliers").then(({ data }) => setData(data)).catch(() => {});
    api.get("/admin/fulfilment").then(({ data }) => setQueue(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const toggle = async (key, connected) => { await api.put("/admin/suppliers/connect", { key, connected }); toast.success(connected ? "Enabled" : "Disabled"); load(); };
  const saveCreds = async (key) => {
    const c = creds[key] || {};
    try {
      await api.put("/admin/suppliers/connect", { key, connected: true, api_endpoint: c.api_endpoint || "", api_key: c.api_key || "" });
      toast.success("Credentials saved — live auto-routing enabled");
      setCreds({ ...creds, [key]: { ...c, api_key: "" } });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const retry = async (orderId) => {
    try { const { data } = await api.post(`/admin/fulfilment/${orderId}/retry`); toast.success(`Routed to ${data.provider || "supplier"} (${data.mode})`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  if (!data) return <div className="py-16 text-center text-slate-500">Loading…</div>;

  const MODE = { live: "bg-[#00E676]/15 text-[#00E676]", simulated: "bg-[#FFB800]/15 text-[#FFB800]" };
  const STAT = { placed: "text-[#00E676]", queued: "text-[#FFB800]", failed: "text-[#FF2A6D]" };

  return (
    <div className="space-y-6" data-testid="admin-suppliers">
      <div className="flex items-start gap-3 rounded-2xl border border-[#8A2BE2]/30 bg-[#8A2BE2]/5 p-4">
        <Bot className="mt-0.5 h-5 w-5 shrink-0 text-[#c79cff]" />
        <p className="text-sm text-slate-300">
          <span className="font-semibold text-white">Auto-routing engine active.</span> Every paid order is automatically packaged with the customer's shipping address + supplier SKUs and sent to <span className="text-[#00F0FF]">TeamDrop</span> first, falling back to <span className="text-[#00F0FF]">AliExpress</span>. Add a supplier's API endpoint + key below to place real orders — until then orders are queued in <span className="text-[#FFB800]">simulation</span> mode with the exact payload ready to send.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {data.connections.map((s) => (
          <div key={s.key} className="dw-card rounded-2xl p-6" data-testid={`supplier-${s.key}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Plug className={`h-5 w-5 ${s.has_credentials ? "text-[#00E676]" : s.connected ? "text-[#FFB800]" : "text-slate-500"}`} /><h4 className="font-display font-semibold text-white">{s.name}</h4></div>
              <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase ${s.priority === 1 ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "bg-white/5 text-slate-400"}`}>{s.priority === 1 ? "Preferred" : "Backup"}</span>
            </div>
            <p className="mt-3 text-xs text-slate-400">{s.note}</p>
            <div className="mt-4 flex items-center justify-between">
              <span className={`font-mono text-xs ${s.has_credentials ? "text-[#00E676]" : "text-slate-500"}`}>{s.has_credentials ? "● Live routing enabled" : s.connected ? "◐ Enabled (simulation)" : "○ Disabled"}</span>
              <Switch checked={s.connected} onCheckedChange={(v) => toggle(s.key, v)} data-testid={`supplier-toggle-${s.key}`} />
            </div>
            <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
              <Label className="text-xs text-slate-400">API endpoint URL</Label>
              <Input value={(creds[s.key]?.api_endpoint) ?? s.api_endpoint ?? ""} onChange={(e) => setCreds({ ...creds, [s.key]: { ...creds[s.key], api_endpoint: e.target.value } })} placeholder="https://api.supplier.com/v1/orders" className="border-white/10 bg-white/5 font-mono text-xs" data-testid={`supplier-endpoint-${s.key}`} />
              <Label className="text-xs text-slate-400">API key {s.has_credentials && <span className="text-[#00E676]">(saved — leave blank to keep)</span>}</Label>
              <Input type="password" value={creds[s.key]?.api_key || ""} onChange={(e) => setCreds({ ...creds, [s.key]: { ...creds[s.key], api_key: e.target.value } })} placeholder="sk_live_…" className="border-white/10 bg-white/5 font-mono text-xs" data-testid={`supplier-key-${s.key}`} />
              <Button onClick={() => saveCreds(s.key)} className="w-full rounded-xl bg-[#8A2BE2] text-white hover:bg-[#9d4ce8]" data-testid={`supplier-save-${s.key}`}><Send className="mr-1 h-4 w-4" /> Save & Enable Live Routing</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="dw-card rounded-2xl p-6" data-testid="fulfilment-queue">
        <h4 className="font-display mb-4 flex items-center gap-2 text-sm font-semibold text-white"><Package className="h-4 w-4 text-[#00F0FF]" /> Fulfilment Queue ({queue.length})</h4>
        {queue.length === 0 ? (
          <p className="text-sm text-slate-500">No routed orders yet. Paid orders will appear here automatically.</p>
        ) : (
          <div className="max-h-[420px] space-y-2 overflow-y-auto">
            {queue.map((q) => (
              <div key={q.order_id} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/5 px-4 py-3 text-sm" data-testid={`fulfilment-${q.order_id}`}>
                <span className="font-mono text-[#00F0FF]">#{q.order_id}</span>
                <span className="text-slate-400">{q.customer_email}</span>
                <span className="text-slate-300">→ {q.provider || "—"}</span>
                <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] uppercase ${MODE[q.mode] || "bg-white/5 text-slate-400"}`}>{q.mode || "—"}</span>
                <span className={`font-mono text-xs ${STAT[q.status] || "text-slate-400"}`}>{q.status}</span>
                {q.supplier_ref && <span className="font-mono text-[10px] text-slate-500">{q.supplier_ref}</span>}
                <button onClick={() => retry(q.order_id)} className="ml-auto flex items-center gap-1 text-xs text-slate-400 hover:text-[#00F0FF]" data-testid={`fulfilment-retry-${q.order_id}`}><RefreshCw className="h-3.5 w-3.5" /> Re-route</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="dw-card rounded-2xl p-6">
        <h4 className="font-display mb-3 text-sm font-semibold text-white">Product Supplier Mappings</h4>
        <div className="max-h-96 space-y-1 overflow-y-auto">
          {data.mappings.map((m) => (
            <div key={m.product_id} className="flex items-center justify-between border-b border-white/5 py-2 text-sm">
              <span className="flex-1 truncate text-slate-300">{m.name}</span>
              <span className="font-mono text-xs text-[#00F0FF]">{m.supplier?.teamdrop_sku || "—"}</span>
              <span className="ml-4 font-mono text-xs text-slate-500">{m.supplier?.aliexpress_id || "—"}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Pricing() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/admin/pricing").then(({ data }) => setP(data)).catch(() => {}); }, []);
  if (!p) return <div className="py-16 text-center text-slate-500">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="admin-pricing">
      <div className="dw-card rounded-2xl p-6">
        <h3 className="font-display text-lg font-semibold text-white">Regional Pricing · Base {p.base}</h3>
        <p className="mt-1 text-sm text-slate-400">All catalog prices are stored in {p.base} and converted live per region.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(p.currencies).map(([code, c]) => (
            <div key={code} className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="font-mono text-lg font-bold text-white">{c.symbol} {code}</p>
              <p className="text-xs text-slate-400">{c.name}</p>
              <p className="mt-1 font-mono text-xs text-[#00F0FF]">1 USD = {c.rate}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="dw-card rounded-2xl p-6">
        <h4 className="font-display text-sm font-semibold text-white">Shipping Rules</h4>
        <p className="mt-2 font-mono text-sm text-slate-300">Free shipping over ${p.shipping.free_threshold_usd} · Flat rate ${p.shipping.flat_usd}</p>
      </div>
    </div>
  );
}

function Seo() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/admin/seo").then(({ data }) => setForm(data)).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/admin/seo", form); toast.success("SEO saved"); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } };
  if (!form) return <div className="py-16 text-center text-slate-500">Loading…</div>;
  return (
    <div className="dw-card rounded-2xl p-6" data-testid="admin-seo">
      <h3 className="font-display mb-4 text-lg font-semibold text-white">Store SEO Metadata</h3>
      <div className="space-y-4">
        <div><Label>Meta Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="seo-title" /></div>
        <div><Label>Meta Description</Label><Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="seo-description" /></div>
        <div><Label>Keywords</Label><Input value={form.keywords || ""} onChange={(e) => setForm({ ...form, keywords: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="seo-keywords" /></div>
        <div><Label>OG Image URL</Label><Input value={form.og_image || ""} onChange={(e) => setForm({ ...form, og_image: e.target.value })} className="mt-1 border-white/10 bg-white/5" data-testid="seo-og" /></div>
        <Button onClick={save} className="rounded-xl bg-[#00F0FF] px-6 text-[#08090E] hover:bg-[#33F3FF]" data-testid="seo-save">Save SEO</Button>
      </div>
    </div>
  );
}

function Team() {
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const load = () => api.get("/admin/team").then(({ data }) => setMembers(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!email.trim()) return;
    try { await api.post("/admin/team", { email: email.trim() }); toast.success("Partner added as Manager"); setEmail(""); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const remove = async (em) => { if (!window.confirm(`Remove ${em}? Their manager access stops immediately.`)) return; await api.delete(`/admin/team/${encodeURIComponent(em)}`); toast.success("Removed"); load(); };
  return (
    <div className="space-y-6" data-testid="admin-team">
      <div className="flex items-start gap-3 rounded-2xl border border-[#8A2BE2]/30 bg-[#8A2BE2]/5 p-4">
        <UserPlus className="mt-0.5 h-5 w-5 shrink-0 text-[#c79cff]" />
        <p className="text-sm text-slate-300">Add a partner's email to give them <span className="font-semibold text-white">Manager</span> access. They sign in themselves (Google or their own password) — you never create or share passwords. Managers can fulfil orders and manage products, but can't see revenue, customers, suppliers or settings.</p>
      </div>
      <div className="dw-card rounded-2xl p-6">
        <Label>Partner email</Label>
        <div className="mt-2 flex gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="partner@example.com" className="border-white/10 bg-white/5" data-testid="team-email" />
          <Button onClick={add} className="rounded-xl bg-[#00F0FF] text-[#08090E] hover:bg-[#33F3FF]" data-testid="team-add"><Plus className="mr-1 h-4 w-4" /> Add Manager</Button>
        </div>
      </div>
      <div className="dw-card rounded-2xl p-6">
        <h4 className="font-display mb-4 text-sm font-semibold text-white">Team Members ({members.length})</h4>
        {members.length === 0 ? <p className="text-sm text-slate-500">No partners yet. Add one above.</p> : (
          <div className="space-y-2">
            {members.map((m) => (
              <div key={m.email} className="flex items-center justify-between rounded-xl border border-white/5 px-4 py-3 text-sm" data-testid={`team-member-${m.email}`}>
                <div>
                  <p className="text-slate-200">{m.name || m.email}</p>
                  <p className="font-mono text-xs text-slate-500">{m.email} · {m.role} · {m.has_account ? "active" : "invited (not signed in yet)"}</p>
                </div>
                <button onClick={() => remove(m.email)} className="text-slate-500 hover:text-[#FF2A6D]" data-testid={`team-remove-${m.email}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const TABS = [
  ["dashboard", "Dashboard", LayoutDashboard, Dashboard, true],
  ["catalog", "Catalog", Boxes, Catalog, false],
  ["orders", "Orders", ShoppingBag, Orders, false],
  ["customers", "Customers", Users, Customers, true],
  ["suppliers", "Suppliers", Plug, Suppliers, true],
  ["pricing", "Pricing / FX", DollarSign, Pricing, true],
  ["seo", "SEO", Search, Seo, true],
  ["team", "Team", UserPlus, Team, true],
];

export default function Admin() {
  const { user } = useAuth();
  const isOwner = ["owner", "admin"].includes(user?.role);
  const visibleTabs = TABS.filter((t) => isOwner || !t[4]);
  const firstTab = visibleTabs[0]?.[0] || "catalog";
  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-2 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#8A2BE2]"><LayoutDashboard className="h-4 w-4" /> {isOwner ? "Owner Console" : "Manager Console"}</div>
      <h1 className="font-display text-3xl font-bold text-white">Store Management</h1>
      <Tabs defaultValue={firstTab} className="mt-8">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-white/5 p-1">
          {visibleTabs.map(([v, label, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="gap-1.5 data-[state=active]:bg-[#00F0FF] data-[state=active]:text-[#08090E]"><Icon className="h-4 w-4" /> {label}</TabsTrigger>
          ))}
        </TabsList>
        {visibleTabs.map(([v, , , Comp]) => (
          <TabsContent key={v} value={v} className="mt-6"><Comp /></TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
